import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Package, RotateCcw, Truck, Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { TASK_TYPE, type Task, type TaskType } from '@klareospace/pharos-lib-types';
import { InlineCopyText, OrderStatusBadge } from '@klareospace/pharos-lib-web-utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TaskDetailHeaderProps {
  readonly copiedTooltip: string;
  readonly copyTooltip: string;
  readonly task: Task;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TASK_TYPE_ICON_MAP: Record<TaskType, LucideIcon> = {
  [TASK_TYPE.DELIVERY]: Truck,
  [TASK_TYPE.NEW_ATTEMPT]: RotateCcw,
  [TASK_TYPE.PICKUP]: Package,
  [TASK_TYPE.RETURN]: Undo2,
};

// ─── Component ────────────────────────────────────────────────────────────────

/** Header for the Task Detail modal: type icon + label, a copyable task id, and the status badge. */
export function TaskDetailHeader({ copiedTooltip, copyTooltip, task }: TaskDetailHeaderProps): React.JSX.Element {
  const { t } = useTranslation('tasks');
  const TypeIcon = TASK_TYPE_ICON_MAP[task.type];

  return (
    <div className="pe-8">
      <div className="flex flex-col space-y-1">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <TypeIcon className="h-5 w-5 text-muted-foreground shrink-0" />
          <span className="text-sm font-semibold text-foreground">{t(`detail.type.${task.type}`)}</span>
          <OrderStatusBadge label={t(`orders:status.${task.status}`)} status={task.status} />
        </div>
        <InlineCopyText
          className="text-xs font-mono text-muted-foreground"
          copiedTooltip={copiedTooltip}
          copyTooltip={copyTooltip}
          value={task.id}
        >
          <span>{task.id}</span>
        </InlineCopyText>
      </div>
    </div>
  );
}
